/** LLM-written prose in this report (decisionReason, per-evidence
 *  description, per-skill rationale) refers to GCA/Comp Way scores in
 *  their native 0–1 form ("0.93", "0.82"). The rest of the report reads
 *  on a 0–5 dot scale, so the raw decimals look like a different unit.
 *  Rewrite any 0.XX pattern in-place to "X.X / 5" so prose matches the
 *  scale the reader is already using. */
export function formatScoresAsDots(text: string): string {
  return text.replace(/\b0\.\d{1,2}\b/g, (match) => {
    const dotValue = parseFloat(match) * 5;
    return `${dotValue.toFixed(1)} / 5`;
  });
}

/**
 * Map of v2 feature names → human-readable pt-br labels. Used by
 * `humanizePredicate` to translate synth-generated rung descriptions
 * (e.g. `max_founded_years >= 0`) into prose the reviewer can read.
 *
 * Keep this list in sync with the predicates that appear in
 * `docs/labs/prompts/scoring-v2/dim-*.yaml`. Features not in the map
 * fall through to a `snake_case → words` fallback in `humanizePredicate`
 * so a missing entry degrades gracefully instead of breaking the UI.
 */
const FEATURE_LABELS_PT_BR: Record<string, string> = {
  // Academic / general
  best_uni_tier: "tier da melhor universidade",
  has_phd: "tem PhD",
  has_mba: "tem MBA",
  has_masters: "tem mestrado",
  has_competitive_course: "fez curso competitivo",

  // Trajectory / tenure
  cum_T1_years: "anos em empresas Tier 1",
  cum_T2_years: "anos em empresas Tier 2",
  max_anchor_years: "maior tempo numa mesma empresa",
  second_anchor_years: "segunda maior tenure",
  career_years: "anos de carreira",
  avg_promo_interval_years: "intervalo médio entre promoções",
  num_strict_promotions: "número de promoções estritas",
  num_employers_with_promotion: "empregadores com promoção",
  has_double_promotion: "teve dupla promoção",
  max_anchor_promotion_count: "promoções na empresa âncora",
  has_lock_in_pattern: "padrão de lock-in de carreira",
  num_red_flag_employers: "empregadores com red flag",
  years_at_small_non_vc_company: "anos em PME sem VC",
  earliest_employee_bucket: "faixa de funcionário inicial",
  earliest_employee_rank: "ranking como funcionário inicial",

  // Founder
  max_founded_years: "maior tempo como fundador",
  num_companies_founded: "empresas fundadas",

  // PM (project management)
  pm_has_executive_role: "teve cargo executivo (PM)",
  pm_has_program_or_pmo: "atuou em programa ou PMO",
  pm_num_management_titles: "títulos de gestão (PM)",
  pm_years_in_management: "anos em gestão (PM)",
  pm_llm_has_director_or_vp_title: "título de diretor/VP (PM)",
  pm_llm_has_multi_team_program_evidence: "evidência de programa multi-time",
  pm_llm_has_owned_function_at_scale: "owner de função em escala",
  pm_llm_years_in_management: "anos em gestão (LLM)",

  // SM (agency to influence stakeholders)
  sm_has_consulting: "passagem por consultoria",
  sm_has_exec_liaison: "ligação executiva (chief of staff etc.)",
  sm_num_client_facing_roles: "papéis client-facing",
  sm_years_client_facing: "anos client-facing",
  sm_llm_has_accelerated_growth_track: "trajetória de crescimento acelerado",

  // BA (business acumen)
  ba_has_finance_field: "atuação em finanças",
  ba_has_top_consulting: "consultoria top-tier",
  ba_has_top_finance: "finanças top-tier",
  ba_years_in_finance: "anos em finanças",
  ba_years_in_strategy: "anos em estratégia",

  // HF (HR fluency)
  hf_has_hr_field: "atuação na área de RH",
  hf_llm_hr_function_years: "anos em função de RH",
  hf_llm_has_run_multiple_hr_subsystems: "atuou em múltiplos sub-sistemas de RH",
  hf_llm_has_extensive_people_management: "gestão extensiva de pessoas",
  hf_llm_has_hr_director_or_higher: "diretoria de RH ou superior",

  // PS (product sense)
  ps_llm_product_or_systems_practice_years: "anos de prática em produto/sistemas",
  ps_llm_has_structured_thinking_evidence: "evidência de pensamento estruturado",
  ps_llm_has_solution_design_evidence: "evidência de design de solução",
  ps_llm_has_systems_thinking_evidence: "evidência de pensamento sistêmico",

  // Top-level dim grades (used in role-level aggregator predicates)
  academic: "nota acadêmica",
  trajectory: "nota de trajetória",
  tenure: "nota de tenure",
  founder: "nota como founder",
  pm: "nota PM",
  sm: "nota SM",
  ba: "nota BA",
  hf: "nota HF",
  ps: "nota PS",
};

export function humanizeFeatureName(raw: string): string {
  const known = FEATURE_LABELS_PT_BR[raw];
  if (known) return known;
  // Fallback: snake_case → spaces, with a couple of well-known prefixes
  // stripped so unknown features still read reasonably.
  return raw
    .replace(/^(hf_llm|pm_llm|sm_llm|ba_llm|ps_llm)_/, "")
    .replace(/^(hf|pm|sm|ba|ps)_/, "")
    .replace(/_/g, " ");
}

export const OPERATOR_LABELS_PT_BR: Record<string, string> = {
  ">=": "≥",
  "<=": "≤",
  "==": "=",
  "!=": "≠",
  ">": ">",
  "<": "<",
};

export function humanizeValue(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "true") return "sim";
  if (trimmed === "false") return "não";
  return trimmed;
}

/**
 * Translate a synth-generated predicate string into a pt-br phrase.
 *
 * Input (from `synthDescription` in server/labs/v2/synth/fit.ts):
 *   "max_founded_years >= 0 AND best_uni_tier >= 2"
 * Output:
 *   "maior tempo como fundador ≥ 0 e tier da melhor universidade ≥ 2"
 *
 * Also recognises the wrapper phrases that `applyLadder` emits:
 *   - "insufficient signal: <features>" → "sinal insuficiente: <labels>"
 *   - "fallback: no rung matched (<desc>)" → "padrão (nenhuma regra ativada): <desc traduzido>"
 *   - "grade N (fallback)" / "fallback (always fires)" → pt-br equivalents
 *
 * Strings without any recognisable predicate shape are returned
 * unchanged so this can be called on free-form LLM descriptions without
 * corrupting them.
 */
export function humanizePredicate(text: string): string {
  if (!text) return text;
  const trimmed = text.trim();

  const insufficient = trimmed.match(/^insufficient\s+signal:\s*(.+)$/i);
  if (insufficient) {
    const features = insufficient[1]
      .split(",")
      .map((f) => humanizeFeatureName(f.trim()))
      .join(", ");
    return `sinal insuficiente: ${features}`;
  }
  const fallback = trimmed.match(/^fallback:\s*no\s+rung\s+matched\s*\((.+)\)$/i);
  if (fallback) {
    return `padrão (nenhuma regra ativada): ${humanizePredicate(fallback[1])}`;
  }
  if (/^grade\s+\d+\s*\(fallback\)$/i.test(trimmed)) {
    return "padrão (nenhuma regra ativada)";
  }
  if (/^fallback\s*\(always\s*fires\)$/i.test(trimmed)) {
    return "padrão (sempre ativa)";
  }

  const parts = trimmed.split(/\s+AND\s+/);
  const PREDICATE_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*(>=|<=|==|!=|>|<)\s*(.+)$/;
  const looksLikePredicate = parts.every((p) => PREDICATE_RE.test(p.trim()));
  if (!looksLikePredicate) return text;

  return parts
    .map((part) => {
      const m = part.trim().match(PREDICATE_RE);
      if (!m) return part;
      const [, feature, op, rawValue] = m;
      const label = humanizeFeatureName(feature);
      const operator = OPERATOR_LABELS_PT_BR[op] ?? op;
      const value = humanizeValue(rawValue);
      return `${label} ${operator} ${value}`;
    })
    .join(" e ");
}
